import fetchParams from './fetch_params'
import { isSameModel } from './model_helpers'
import ActivityFlow from '@polleverywhere/core/lib/models/activity_flow'

const MQA_TYPES = ['competition', 'survey']

function isModelInList (model, list) {
  return list.some((element) => isSameModel(model, element))
}

/* Let me explain the reasoning behind the following shenanigans...
 *
 * If we want to "act on" an activity in any way (e.g. edit it, clear
 * responses, etc.), and that activity is part of an MQA, we're asked to
 * act on the parent MQA instead. For instance, directly editing an MCP
 * that's part of a competition is actually undefined behavior; you need
 * to edit the competition instead and change the question through the
 * competition editor.
 *
 * So, for a given list of activities, we need to end up with the
 * corresponding "top-level" activities and go forward with those.
 */
export default async function (models = []) {
  const additionalFetches = []

  const finalModels = models.reduce((modelsSoFar, model) => {
    if (model === 'join_instructions') {
      modelsSoFar.push(model) // join instructions are already top-level
      return modelsSoFar
    }

    if (model instanceof ActivityFlow) {
      modelsSoFar.push(model.get('activity')) // activity flows are already top-level
      return modelsSoFar
    }

    if (MQA_TYPES.includes(model.type())) {
      modelsSoFar.push(model) // a survey or competition is already top-level
      return modelsSoFar
    }

    const parentActivity = model.competition || model.survey
    if (!parentActivity) {
      modelsSoFar.push(model) // an activity that doesn't belong to any MQA is also top-level
      return modelsSoFar
    }

    // If we're here, we have a parent activity.
    // We'd like to drop the current activity on the floor
    // and track our parent activity in the resulting list.

    // There is a chance the parent activity is already in the
    // resulting list though, and we don't want to duplicate it.
    if (!isModelInList(parentActivity, modelsSoFar)) {
      modelsSoFar.push(parentActivity)

      // An activity given at the `competition` or `survey` property
      // of another activity is not hydrated. Yet we'd like to exit
      // this stage of the pipeline with hydrated models.
      additionalFetches.push(parentActivity.fetch(fetchParams))
    }

    return modelsSoFar
  }, [])

  await Promise.all(additionalFetches)

  return finalModels
}
